import moment from "moment";
import { useMemo } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentState,
  AssessmentType,
  GetAssessmentQuery,
} from "types/graphql-schema";

import { getProgramUrl } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import RelatedSubmissions from "./related-submissions/related-submissions";
import { RelatedSubmissionsLocation } from "./related-submissions/types";

const AssessmentHeader = ({
  myAssessmentsData,
}: {
  myAssessmentsData?: GetAssessmentQuery;
}) => {
  const currentUser = currentUserVar();
  const label = useLabel();

  const assessment = useMemo(
    () => (myAssessmentsData ? myAssessmentsData.assessment : null),
    [myAssessmentsData],
  );
  const complianceProgram = useMemo(
    () => (assessment ? assertNonNull(assessment.complianceProgram) : null),
    [assessment],
  );
  const canReadProgramDetails = useMemo(
    () => complianceProgram?.canReadDetails.permission,
    [complianceProgram],
  );

  const isEngagementSurvey = useMemo(
    () =>
      assessment?.template.assessmentType === AssessmentType.EngagementSurvey,
    [assessment],
  );
  const target = useMemo(
    () =>
      !isEngagementSurvey && assessment
        ? assertNonNull(assessment.target)
        : null,
    [assessment, isEngagementSurvey],
  );
  const responder = useMemo(() => assessment?.responder ?? null, [assessment]);
  const isSelfAssessment = useMemo(
    () => target?.id === responder?.id,
    [responder, target],
  );
  const assessmentLabel = useMemo(() => {
    if (isEngagementSurvey) {
      return "Survey";
    }
    return "review";
  }, [isEngagementSurvey]);

  const getHeaderTitle = useMemo(() => {
    if (isEngagementSurvey) {
      return label(assessmentLabel, { capitalize: true });
    }

    const reviewTypeLabel = getAssessmentTypeLabel(
      assertNonNull(assessment?.template.assessmentType),
      isSelfAssessment,
    );

    return `${reviewTypeLabel} ${label("review", { capitalize: true })}: ${
      target?.name
    }`;
  }, [
    isEngagementSurvey,
    assessment?.template.assessmentType,
    target?.name,
    isSelfAssessment,
    label,
    assessmentLabel,
  ]);

  if (!assessment) {
    return null;
  }

  const leftCellClassName = "py-2 font-medium w-[200px]";
  const rightCellClassName = "py-2";

  return (
    <div className={classNames("mb-12 text-sm pb-6 border-b")}>
      <div className="flex flex-col gap-6 w-full mx-auto max-w-screen-lg">
        <div className="flex items-center gap-2">
          {!isEngagementSurvey && <Avatar user={target} size={8} />}
          <h2 className="text-2xl font-bold text-gray-800">{getHeaderTitle}</h2>
        </div>

        <div>
          <Layout.MainSubSection
            title={`${label(assessmentLabel, { capitalize: true })} Details`}
            collapsible
            defaultIsExpanded
          >
            <table className="text-gray-600 w-full">
              <tbody>
                <tr>
                  <td className={leftCellClassName}>Program</td>
                  {canReadProgramDetails ? (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "py-0", // to compensate for button padding
                      )}
                    >
                      <Button
                        to={getProgramUrl(assertNonNull(complianceProgram).id, {
                          isSurvey: isEngagementSurvey,
                        })}
                        theme={buttonTheme.text}
                        leftNegativeMargin
                      >
                        <TopicflowIcon
                          type="program"
                          className="shrink-0 w-4 h-4"
                        />
                        {complianceProgram?.title}
                      </Button>
                    </td>
                  ) : (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "flex items-center gap-1",
                      )}
                    >
                      <TopicflowIcon
                        type="program"
                        className="shrink-0 w-4 h-4"
                      />
                      {complianceProgram?.title}
                    </td>
                  )}
                </tr>

                {!isEngagementSurvey && isSelfAssessment && (
                  <tr>
                    <td className={leftCellClassName}>Subject</td>
                    <td className={rightCellClassName}>
                      <div className="flex items-center">
                        <Avatar
                          className="mr-1"
                          user={assertNonNull(target)}
                          size="5"
                        />{" "}
                        {`${target?.name} (Self ${label("review", {
                          capitalize: true,
                        })})`}
                      </div>
                    </td>
                  </tr>
                )}

                {isEngagementSurvey && (
                  <tr>
                    <td className={leftCellClassName}>Responder</td>
                    <td className={rightCellClassName}>
                      <div className="flex items-center">
                        <Avatar className="mr-1" user={responder} size="5" />{" "}
                        {responder?.name ?? "Anonymous"}
                      </div>
                    </td>
                  </tr>
                )}

                {!isEngagementSurvey && !isSelfAssessment && (
                  <>
                    <tr>
                      <td className={leftCellClassName}>Responder</td>
                      <td className={rightCellClassName}>
                        <div className="flex items-center">
                          <Avatar className="mr-1" user={responder} size="5" />{" "}
                          {responder?.name ?? "Anonymous"}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className={leftCellClassName}>Subject</td>
                      <td className={rightCellClassName}>
                        <div className="flex items-center">
                          <Avatar
                            className="mr-1"
                            user={assertNonNull(target)}
                            size="5"
                          />{" "}
                          {target?.name}
                        </div>
                      </td>
                    </tr>
                    {target?.organizationSpecificInfo?.startDate && (
                      <tr>
                        <td className={leftCellClassName}>
                          Subject's Start Date
                        </td>
                        <td className={rightCellClassName}>
                          {moment(
                            target?.organizationSpecificInfo?.startDate,
                          ).format("MMM D, YYYY")}
                        </td>
                      </tr>
                    )}
                    {assessment.targetPosition && (
                      <tr>
                        <td className={leftCellClassName}>Position</td>
                        <td className={rightCellClassName}>
                          {assessment.targetPosition}
                        </td>
                      </tr>
                    )}
                  </>
                )}

                {!isEngagementSurvey && (
                  <tr>
                    <td className={leftCellClassName}>Program period</td>
                    <td className={rightCellClassName}>
                      {assessment?.periodStartDate && assessment?.periodEndDate
                        ? `${moment(assessment.periodStartDate).format(
                            "MMM D, YYYY",
                          )} - ${moment(assessment.periodEndDate).format(
                            "MMM D, YYYY",
                          )}`
                        : "Not set"}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className={leftCellClassName}>Due date</td>
                  <td className={rightCellClassName}>
                    {moment(assessment?.dueDate).format("MMM D, YYYY")}
                  </td>
                </tr>
                {assessment.state === AssessmentState.Submitted && (
                  <tr>
                    <td className={leftCellClassName}>Submitted</td>
                    <td className={rightCellClassName}>
                      <div className="text-green-700">
                        {moment(assessment.submittedDatetime).format(
                          "MMM D, YYYY @ h:mma",
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Layout.MainSubSection>

          {!isEngagementSurvey &&
            assessment.target &&
            assessment.complianceProgram && (
              <RelatedSubmissions
                location={
                  assessment.state === AssessmentState.Submitted
                    ? RelatedSubmissionsLocation.SUBMITTED_ASSESSMENT
                    : RelatedSubmissionsLocation.DRAFT_ASSESSMENT
                }
                assessmentType={assessment.template.assessmentType}
                complianceProgramId={assessment.complianceProgram.id}
                target={assessment.target}
              />
            )}
        </div>

        {[
          AssessmentGroupAnonymity.SemiAnonymous,
          AssessmentGroupAnonymity.Anonymous,
        ].includes(assessment.template.anonymity) &&
          assessment.state !== AssessmentState.Submitted &&
          currentUser.id === responder?.id &&
          !isSelfAssessment && (
            <div className="border rounded-md border-gray-200 text-gray-500 py-3 px-4">
              <TopicflowIcon
                type="lockClosed"
                className="h-4 w-4 inline mr-1"
              />{" "}
              Your responses will appear anonymous
              {assessment.template.anonymity ===
              AssessmentGroupAnonymity.SemiAnonymous
                ? " to the subject"
                : ""}
              .{" "}
              {isEngagementSurvey
                ? "They will be shared with your upward reporting line and admins. "
                : ""}
              Anonymity will be affected if you put identifiable content in your
              comments.
            </div>
          )}
      </div>
    </div>
  );
};

export default AssessmentHeader;
